import { ANYONE } from 'Calendar/anyone';
import { Form } from 'antd';
import Box from 'components/Box';
import { loadingSignal } from 'components/Loading';
import { QUICK_BOOKING_INDEX } from 'constants/variables';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import aptActions from 'services/actions/apt';
import shopSelectors from 'services/selectors/shop';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import toast from 'utils/toast';
import StaffAvailablePicker from 'widgets/Staff/StaffAvailablePicker';
import CustomerQuickBookingSection from './CustomerSection';
import GroupNumberSection from './GroupNumberSection';
import InputNote from './InputNote';
import Modal from './Modal';
import { useServicesSection } from './Services';
import StaffServicesSection from './StaffServicesSection';
import TimeSection from './Time';
import qbSignal, { IQuickBookRef, IQuickBookStaffSelected } from './signal';
import iconPlus from './icon-plus.svg';

const QuickBookingBox = forwardRef<IQuickBookRef>((_, ref) => {
  const dispatch = useAppDispatch();
  const [formStaff] = Form.useForm();
  const _staffStore = shopSelectors.staff();
  const serRef = useServicesSection();
  const handleClose = () => qbSignal.close();
  const handleBlockHours = async () => {
    try {
      await formStaff.validateFields();
    } catch (error) {
      toast.error('Please fill in all fields');
      return;
    }

    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');

    const values = qbSignal.getValues(staffsSelected || []);
    const isEmpty = values.some(s => !s.services.length);
    if (isEmpty) {
      toast.info('Please select at least one service');
      return serRef.current?.waring();
    }
    dispatch(aptActions.quickBooking.doQuickBooking(values));
  };

  const staffStore = useMemo(() => [ANYONE].concat(_staffStore), [_staffStore]);

  const handleAddMoreStaff = (add: (options: Partial<IQuickBookStaffSelected>) => void) => async () => {
    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');
    const queryData = qbSignal.getCurrentStartEndTime();
    const currentStaff = await StaffAvailablePicker.getAvailableWithTime(queryData, staffsSelected?.map(o => o.id?.toString() || '') || []);
    if (!currentStaff) return;
    add({
      id: currentStaff.id,
      services: [],
      requestStaff: qbSignal.requestStaff.value,
    });
  };

  const handleOnChangeStaff = async (value?: IQuickBookStaffSelected, onChange?: (value: IQuickBookStaffSelected) => void) => {
    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');
    const queryData = qbSignal.getCurrentStartEndTime();
    const currentStaff = await StaffAvailablePicker.getAvailableWithTime(queryData, staffsSelected?.map(o => o.id?.toString() || '') || []);
    if (!currentStaff) return;
    onChange?.({
      ...value || {},
      id: currentStaff.id,
    } as IQuickBookStaffSelected);
  };


  useImperativeHandle(ref, () => ({
    initFormService: (data: IQuickBookStaffSelected[]) => {
      formStaff.setFieldsValue({
        staffsSelected: data,
      });
    }
  }));

  return (
    <Modal
      title="QUICK BOOKING"
      open={qbSignal.openSignal.value}
      onCancel={handleClose}
      okText={'Booking'}
      onOk={handleBlockHours}
      height='auto'
      width='90vw'
      loading={loadingSignal.value}
      style={{ zoom: 0.8 }}
      visibleClose
      zIndex={QUICK_BOOKING_INDEX}
    >
      <FormStyled>
        <div className='customer-staff'>
          <CustomerQuickBookingSection />
          <GroupNumberSection />
        </div>
        <TimeSection />
        <InputNote />
        <Form form={formStaff}>
          <Form.List name={'staffsSelected'}>
            {(fields, { add, remove }) => {
              return <Box className='section-wrapper' display='flex' flexDirection='column' gap='4'>
                <Box display='flex' flexDirection='column' gap='6'>
                  {fields.map(field => (
                    <Form.Item key={field.key} name={field.name} noStyle>
                      <StaffServicesSection
                        handleOnChangeStaff={handleOnChangeStaff}
                        disabledRemove={(fields.length || 0) <= 1}
                        remove={() => remove(field.name)}
                        name={field.name}
                        staffStore={staffStore}
                      />
                    </Form.Item>
                  ))}
                </Box>
                <Box display='flex' alignItems='center' justifyContent='start'>
                  <button onClick={handleAddMoreStaff(add)} type='button' className='btn-add-more'>
                    <img src={iconPlus} alt='icon-plus' />
                    <span>Add More Staff</span>
                  </button>
                </Box>
              </Box>;
            }}
          </Form.List>
        </Form>
      </FormStyled>
    </Modal>
  );
});

export default QuickBookingBox;

const FormStyled = styled.div`
padding: 1rem 1.5rem;
flex: 1;
gap: 8px;
display: flex;
flex-direction: column;
  .section-wrapper {
    background: #fff;
    border-radius: 8px;
    padding: 8px 16px;
    gap: 8px;
    flex: 1 0 0;
  }

  .customer-staff {
    display: flex;
    gap: 24px;
    .customer-staff-item {
      flex: 1;
    }
  }
  .section-label {
   color: #7B7B7B;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 120% */
    &.mb {
    margin-bottom: 8px;
    }
  }

  .btn-add-more {
    display: flex;
    height: 48px;
    padding: 0px 8px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 4px;
    background: #148CF1;
    box-shadow: 0 4px 8px -3px rgba(45, 35, 66, 0.30);
    span {
      color: #FFF;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;